/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @LastEditors: Ryan
 * @LastEditTime: 2026-02-05
 * @Description: 视频翻译 Landing 页面入口 - Spanish to English
 */
import type { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';

import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead, { type SEOHeadProps } from '@/landing/components/SEOHead';
import type { FAQItem } from '@/landing/components/templates/FAQSection';
import type { FeatureItem } from '@/landing/components/templates/FeaturesSection';
import HeroSection from '@/landing/components/templates/HeroSection';
import type { StepData } from '@/landing/components/templates/HowToSection';
import type { UseCaseItem } from '@/landing/components/templates/UseCasesSection';
import type { ReasonItem } from '@/landing/components/templates/WhyChooseSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '@/landing/utils/structuredData';
import { getAllSlugs, getLandingConfig } from '@/landing/video-translator/config';

// 非首屏组件懒加载（代码拆分，减少首屏 JS 体积）
const VideoTranslationDemos = dynamic(
  () => import('@/landing/components/templates/VideoTranslationDemos'),
);
const FeaturesSection = dynamic(
  () => import('@/landing/components/templates/FeaturesSection'),
);
const HowToSection = dynamic(() => import('@/landing/components/templates/HowToSection'));
const UseCasesSection = dynamic(
  () => import('@/landing/components/templates/UseCasesSection'),
);
const LanguageListSection = dynamic(
  () => import('@/landing/components/templates/LanguageListSection'),
);
const WhyChooseSection = dynamic(
  () => import('@/landing/components/templates/WhyChooseSection'),
);
const VsSection = dynamic(() => import('@/landing/components/templates/VsSection'));
const ExploreToolsSection = dynamic(
  () => import('@/landing/components/templates/ExploreToolsSection'),
);
const FAQSection = dynamic(() => import('@/landing/components/templates/FAQSection'));
const FooterCTASection = dynamic(
  () => import('@/landing/components/templates/FooterCTASection'),
);

// ============ Static Content - Spanish to English ============

const PAGE_URL = 'https://falcocut.ai/tools/video-translator/spanish-to-english';

// Hero Section
const HERO_CONTENT = {
  title: 'Translate Spanish Video to English Online with AI',
  subtitle:
    'Translate Spanish video to English in FalcoCut by uploading your file. FalcoCut auto-translates voice and subtitles, adds lip sync, and lets you choose bilingual or translated-only subtitles with easy style controls.',
  cta: 'Upload File',
  sourceLanguage: 'Spanish',
  targetLanguage: 'English',
};

// Video Translation Demos
const DEMOS_CONTENT = {
  title: 'See Spanish Clips Shift Into Clear English Versions',
  subtitle:
    'FalcoCut shows a side-by-side preview, so you can watch your Spanish video become an English version in minutes. Keep subtitles and voice clear and easy to follow, even if you are new to video editing.',
  cta: 'Translate Spanish Video',
};

// Features Section
const FEATURES_CONTENT = {
  title: 'Turn Spanish Videos Into Clear English Stories Fast',
  subtitle:
    'FalcoCut helps you translate a video from Spanish to English without complex tools or editing skills. Get clean subtitles, natural voice, and a polished look that feels ready to post.',
  cta: 'Translate Spanish Video',
};

const FEATURE_IMAGES = [
  'https://assets.falcocut.ai/tools/video-translator/translate-into-different-languages-instantly.webp',
  'https://assets.falcocut.ai/tools/video-translator/extensive-library-of-authentic-ai-voices.webp',
  'https://assets.falcocut.ai/tools/video-translator/ai-voice-cloning-and-natural-dubbing.webp',
  'https://assets.falcocut.ai/tools/video-translator/accurate-subtitles-with-editing-options.webp',
  'https://assets.falcocut.ai/tools/video-translator/seamless-lip-sync-for-realism.webp',
  'https://assets.falcocut.ai/tools/video-translator/all-in-one-video-localization-platform.webp',
];

const FEATURES: FeatureItem[] = [
  {
    title: 'More Languages Ready When Your Channel Grows',
    description:
      'Reach more viewers without changing your content plan. FalcoCut supports 35 languages for subtitles, voice, and lip sync. That means you can scale beyond Spanish and English later. You can also create up to two target languages in one run. This saves time when you post to more than one market. Your message stays clear across regions and audiences.',
    imageUrl: FEATURE_IMAGES[0],
    imageAlt: 'More Languages Ready When Your Channel Grows',
  },
  {
    title: 'Natural Voices That Sound Like Real People',
    description:
      'Bad dubbing can make viewers leave in seconds. FalcoCut offers a wide voice library with realistic tone and pacing. You can match the voice to your video style and audience. This helps your English version feel smooth and easy to trust. Clear speech also improves watch time and understanding. Your content keeps its energy, even after translation.',
    imageUrl: FEATURE_IMAGES[1],
    imageAlt: 'Natural Voices That Sound Like Real People',
  },
  {
    title: 'Dubbing Options for a Stronger Brand Sound',
    description:
      'Keep a consistent sound across every translated video. FalcoCut supports AI dubbing and AI voice cloning for a familiar feel. This is helpful for creators, teachers, and teams with a known voice. Your English track can stay close to the original delivery. That makes your message feel more personal and less generic. Viewers get a version that still feels like you.',
    imageUrl: FEATURE_IMAGES[2],
    imageAlt: 'Dubbing Options for a Stronger Brand Sound',
  },
  {
    title: 'Accurate Subtitles You Can Edit and Style',
    description:
      'Subtitles help people watch anywhere, even with sound off. FalcoCut detects speech and can also read on-screen text. It creates Spanish to English subtitles you can refine for names and slang. You can choose bilingual lines or translated-only captions. Style controls let you adjust font, size, color, and placement. The result looks clean on social feeds and longer videos.',
    imageUrl: FEATURE_IMAGES[3],
    imageAlt: 'Accurate Subtitles You Can Edit and Style',
  },
  {
    title: 'Lip Sync That Keeps Faces Looking Natural',
    description:
      'When lips do not match, videos can feel fake and distracting. FalcoCut lip sync aligns mouth movement to the new English voice. This works great for talking-head clips, interviews, and lessons. It helps viewers focus on the message, not the mismatch. The video feels more like a true native version. You get a smoother, more professional final result.',
    imageUrl: FEATURE_IMAGES[4],
    imageAlt: 'Lip Sync That Keeps Faces Looking Natural',
  },
  {
    title: 'One Simple Workspace for Full Video Localization',
    description:
      'You should not need five tools to finish one translated video. FalcoCut brings subtitles, voice, and export into one flow. You can manage versions and download results from My Creations. This keeps your projects organized as you publish more content. You can start in a browser and skip heavy setup. Faster turnaround helps you post more often and grow sooner.',
    imageUrl: FEATURE_IMAGES[5],
    imageAlt: 'One Simple Workspace for Full Video Localization',
  },
];

// How To Section
const HOW_TO_CONTENT = {
  title: 'How to Translate Spanish Video to English in FalcoCut',
  cta: 'Start Translating',
};

const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Step 1: Upload Your Clip',
    description:
      'Click Add Video to Translate, or choose Select From Library to pick a saved file. Upload an MP4 or MOV, then wait a moment for the preview to load.',
  },
  {
    number: 2,
    title: 'Step 2: Choose Source and Target Languages',
    description:
      'Set Spanish as the Source Language, then pick English as your Target Language. You can choose up to two targets to create more than one version in one run.',
  },
  {
    number: 3,
    title: 'Step 3: Review Subtitles and Voice',
    description:
      'Turn on Translate Subtitles to capture speech and on-screen text, then check the preview for timing. If you want dubbing, enable Translate Voice, pick a voice, and add Lip Sync.',
  },
  {
    number: 4,
    title: 'Step 4: Export and Share Your Video',
    description:
      'Pick Bilingual Subtitles or Translated Only, then adjust font, size, color, and position. When it is ready, open My Creations and download with a watermark or upgrade for no watermark.',
  },
];

// Use Cases Section
const USE_CASES_CONTENT = {
  title: 'Unlock English Reach Across Real-World Video Needs',
};

const USE_CASES: UseCaseItem[] = [
  {
    title: 'E-Commerce',
    description:
      'Sell to English buyers without reshooting your Spanish product videos. Add clear English subtitles or a natural-sounding dub to explain features fast. Lip sync helps demos feel local and keeps trust high. Faster localization means more listings ready for more shoppers.',
  },
  {
    title: 'Global Education',
    description:
      'Turn Spanish lessons into English versions that more students can follow. Use bilingual subtitles to support learning and improve understanding for mixed-level classes. English voice translation keeps attention on long lectures and tutorials. Clear access helps reduce drop-offs and boosts completion rates.',
  },
  {
    title: 'Social Creators',
    description:
      'Grow beyond Spanish-only feeds by posting English-ready clips in minutes. English subtitles make short videos easier to watch on mute and share. Voice translation helps your message land with new viewers across platforms. More clarity means more comments, saves, and repeat views.',
  },
  {
    title: 'Business Marketing',
    description:
      'Localize Spanish promos for English audiences while keeping the same visuals and timing. Add English subtitles for fast scanning, or use dubbing for a stronger pitch. Lip sync makes spokesperson videos feel more natural and polished. Better understanding leads to higher engagement and more leads.',
  },
  {
    title: 'Event Collaboration',
    description:
      'Share Spanish talks with English partners so everyone stays aligned. English subtitles improve clarity for remote teams and noisy venues. Voice translation makes key points easier to follow during recaps and highlights. Faster turnaround keeps projects moving and avoids repeated explanations.',
  },
  {
    title: 'Media & Podcasts',
    description:
      'Make Spanish interviews accessible to English listeners without changing the original structure. Add English subtitles for video podcasts and social cutdowns. Use English dubbing when you want a hands-free listening experience. Clear translation helps audiences stay longer and understand every key moment.',
  },
];

// Language List Section
const LANGUAGE_LIST_CONTENT = {
  title: 'Unlock 35 Languages for Fast Video Localization',
  subtitle:
    'FalcoCut supports 35 languages, so you can turn one video into versions for new viewers in minutes. Upload your file, pick your languages, and export with subtitles and voice in a few clicks.',
  cta: 'Translate Spanish Video',
};

// Why Choose Section
const WHY_CHOOSE_CONTENT = {
  title: 'Count on FalcoCut for Fast, Accurate Spanish-to-English Results',
  subtitle:
    'FalcoCut helps you translate Spanish video to English in minutes, without complex editing steps. It runs in your browser and keeps your workflow simple, steady, and secure from upload to download.',
  cta: 'Translate Spanish Video to English Now',
};

const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Start in Your Browser, Not a Complex Editor',
    description:
      'FalcoCut works right in your browser, so you can begin without installs or setup delays. The layout is clean and easy for first-time users. Upload your MP4 or MOV, preview it, and move on fast. You spend less time learning tools and more time publishing.',
  },
  {
    title: 'Get English Versions Ready in Minutes',
    description:
      'When you need Spanish to English video translation, speed matters for trends and deadlines. FalcoCut processes quickly so you can review results sooner. Make a change, re-run, and keep moving without long waits. It is built to save time on every project.',
  },
  {
    title: 'Choose Subtitles, Voice, or Both for Each Clip',
    description:
      'FalcoCut lets you translate Spanish video to English subtitles, voice, or both based on your goal. Use subtitles for quiet viewing, or add voice for a full English experience. You can also create up to two target languages in one run. That flexibility helps you fit ads, lessons, and social posts.',
  },
  {
    title: 'Clear Output You Can Trust for Meaning and Tone',
    description:
      'Good translation should keep the message clear, not just swap words. FalcoCut is designed to produce readable English that matches the intent of Spanish speech. It also recognizes on-screen text when creating subtitles. You get cleaner results with fewer manual fixes afterward.',
  },
  {
    title: 'Keep Costs Low While Scaling More Videos',
    description:
      'If you translate a video from Spanish to English often, costs can add up fast. FalcoCut helps you scale without hiring extra help for every clip. You can download a free version with a watermark to test your workflow. Upgrade only when you need a clean export for publishing.',
  },
  {
    title: 'Handle Your Files in a Safer, More Controlled Flow',
    description:
      'Your videos can include private voices, faces, and brand details that need care. FalcoCut keeps the process simple, so you always know what you uploaded and what you will download. You can review the preview before exporting the final file. That control helps you work with more confidence.',
  },
];

// VS Section
const VS_CONTENT = {
  title: 'Manual Video Translation vs. FalcoCut AI Localization',
  subtitle:
    'Manual work takes time, money, and lots of back-and-forth. FalcoCut helps you scale faster with automated subtitles, voice, and export in one place.',
  oldTitle: 'Traditional Manual Workflow',
  oldPoints: [
    'Slow back-and-forth edits',
    'Higher costs per video',
    'Hard to keep tone consistent',
    'Many tools to manage',
    'Long waits for each update',
  ],
  oldConclusion: 'Manual work drags on and gets expensive fast.',
  newTitle: 'FalcoCut Automation',
  newPoints: [
    'Fast processing from one upload',
    'Lower cost for more videos',
    'Subtitles and AI voice in one flow',
    'Lip sync option for talking videos',
    'Supports 30+ languages at once',
  ],
  newConclusion: 'FalcoCut delivers clean results in minutes, not days.',
  cta: 'Translate Spanish Video',
};

// FAQ Section
const FAQ_CONTENT = {
  title: 'Frequently Asked Questions',
};

const FAQS: FAQItem[] = [
  {
    question: 'How do I translate a Spanish video to English in FalcoCut?',
    answer:
      'Upload your MP4 or MOV, then choose Spanish as the source language and English as your target language. Turn on Translate Subtitles, Translate Voice, or both, depending on what you need. After processing, open My Creations to download your English version with your chosen subtitle style and voice.',
  },
  {
    question:
      'Can I get English subtitles from a Spanish video without dubbing the voice?',
    answer:
      'Yes, you can translate Spanish to English subtitles without changing the original audio. Enable Translate Subtitles and keep Translate Voice turned off to preserve the Spanish voice. You can also choose bilingual subtitles or translated-only subtitles and adjust font, size, color, and position.',
  },
  {
    question: 'Does FalcoCut support Spanish to English voice dubbing and lip sync?',
    answer:
      "Yes, Spanish and English are fully supported for voice translation, AI dubbing, and lip sync. Turn on Translate Voice to generate an English voice track, then enable Lip Sync for a more natural on-camera result. You can pick a voice style that fits the speaker's tone and age for better realism.",
  },
  {
    question: 'Can I translate a video from Spanish to English free?',
    answer:
      'You can process a translation and download a version with a watermark for free. If you need a clean export, you can upgrade to download without a watermark. This lets you test subtitle translation, voice dubbing, and styling before paying for final delivery.',
  },
  {
    question: 'How long does Spanish to English video translation take?',
    answer:
      "Processing time depends on video length and which options you enable, such as subtitles, voice, and lip sync. Short clips can finish quickly, while longer videos may take more time to generate and sync. Because FalcoCut runs in your browser, you can start a job and come back when it's ready.",
  },
  {
    question: 'What video formats can I upload for Spanish to English translation?',
    answer:
      'FalcoCut supports MP4 and MOV uploads for translation. After you upload, a preview appears automatically so you can confirm the content before processing. This keeps the workflow simple for beginners who just want to translate a Spanish video to English fast.',
  },
  {
    question:
      'Can I create two English versions at once, like subtitles plus another language?',
    answer:
      'Yes, you can choose up to two target languages in one run. For example, you can generate an English version and a second language version from the same Spanish source video. This is helpful when you publish to multiple channels and want consistent timing and formatting across outputs.',
  },
  {
    question:
      'How accurate is the Spanish to English translation for subtitles and dubbing?',
    answer:
      'FalcoCut uses AI to detect speech and generate Spanish to English subtitles and voice translation. Results are usually strong for clear audio, but accents, background noise, or fast speech can affect accuracy. You can improve quality by choosing the right voice style and using subtitle display options that match your audience.',
  },
  {
    question: 'Can I change how the English subtitles look on the final video?',
    answer:
      'Yes, you can control subtitle display and styling before you export. Choose bilingual subtitles to show Spanish and English together, or pick translated-only subtitles for a clean English view. You can also adjust font, size, color, position, and background to match your brand or platform.',
  },
  {
    question: 'Is my Spanish video safe when I upload it for English translation?',
    answer:
      'FalcoCut is built to handle your videos and data securely during upload and processing. You stay in control of what you upload and what you download, and you can remove files from your workflow by managing your creations. This helps you translate Spanish videos to English with confidence, even for client or business content.',
  },
];

// Footer CTA Section
const FOOTER_CTA_CONTENT = {
  title: 'Convert Spanish Clips to English Today for Free',
  subtitle:
    'Use FalcoCut to translate spanish video to english in minutes—upload your MP4 or MOV, generate English subtitles and voice, add lip sync, and export with bilingual or translated-only captions.',
  cta: 'Translate Spanish Video',
};

// SEO Config
const SEO_CONFIG: SEOHeadProps = {
  title: 'Translate Spanish Video to English Instantly Online',
  description:
    'Translate Spanish video to English with FalcoCut. Fast Spanish to English video translation with subtitles—easy steps for beginners, no editing needed.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut Video Translator - Spanish to English',
  publishedTime: '2026-01-08',
  modifiedTime: '2026-02-05',
  preloadImages: [FEATURE_IMAGES[0]],
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut Video Translator - Spanish to English',
      description:
        'Translate Spanish video to English with FalcoCut. Fast Spanish to English video translation with subtitles—easy steps for beginners, no editing needed.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.9',
      ratingCount: '2350',
      featureList: [
        'AI Video Translation',
        'Voice Cloning & Dubbing',
        'Auto Subtitles',
        'Lip Sync',
        'Multi-language Support',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQS.filter((faq) => faq.answer).map((faq) => ({
        question: faq.question,
        answer: faq.answer as string,
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'Video Translator', url: 'https://falcocut.ai/video-translator' },
        { name: 'Spanish to English', url: PAGE_URL },
      ],
    }),
  ],
};

// ============ Page Component ============

interface LandingPageProps {
  slug: string;
}

const VideoTranslatorLandingPage: React.FC<LandingPageProps> = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />

      <HeroSection
        title={HERO_CONTENT.title}
        subtitle={HERO_CONTENT.subtitle}
        ctaText={HERO_CONTENT.cta}
        sourceLanguageLabel="Source Language"
        sourceLanguageDefault={HERO_CONTENT.sourceLanguage}
        targetLanguageLabel="Target Language"
        targetLanguageDefault={HERO_CONTENT.targetLanguage}
      />

      <VideoTranslationDemos
        title={DEMOS_CONTENT.title}
        subtitle={DEMOS_CONTENT.subtitle}
        ctaText={DEMOS_CONTENT.cta}
      />

      <FeaturesSection
        title={FEATURES_CONTENT.title}
        subtitle={FEATURES_CONTENT.subtitle}
        features={FEATURES}
        ctaText={FEATURES_CONTENT.cta}
      />

      <HowToSection
        title={HOW_TO_CONTENT.title}
        steps={HOW_TO_STEPS}
        ctaText={HOW_TO_CONTENT.cta}
      />

      <UseCasesSection title={USE_CASES_CONTENT.title} useCases={USE_CASES} />

      <LanguageListSection
        title={LANGUAGE_LIST_CONTENT.title}
        subtitle={LANGUAGE_LIST_CONTENT.subtitle}
        ctaText={LANGUAGE_LIST_CONTENT.cta}
      />

      <WhyChooseSection
        title={WHY_CHOOSE_CONTENT.title}
        subtitle={WHY_CHOOSE_CONTENT.subtitle}
        reasons={WHY_CHOOSE_REASONS}
        ctaText={WHY_CHOOSE_CONTENT.cta}
      />

      <VsSection
        title={VS_CONTENT.title}
        subtitle={VS_CONTENT.subtitle}
        oldTitle={VS_CONTENT.oldTitle}
        oldPoints={VS_CONTENT.oldPoints}
        oldConclusion={VS_CONTENT.oldConclusion}
        newTitle={VS_CONTENT.newTitle}
        newPoints={VS_CONTENT.newPoints}
        newConclusion={VS_CONTENT.newConclusion}
        ctaText={VS_CONTENT.cta}
      />

      <ExploreToolsSection />

      <FAQSection title={FAQ_CONTENT.title} faqs={FAQS} />

      <FooterCTASection
        title={FOOTER_CTA_CONTENT.title}
        subtitle={FOOTER_CTA_CONTENT.subtitle}
        ctaText={FOOTER_CTA_CONTENT.cta}
      />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllSlugs();

  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<LandingPageProps> = async ({ params }) => {
  const slug = params?.slug as string;
  const config = getLandingConfig(slug);

  if (!config) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      slug,
    },
    revalidate: 3600,
  };
};

export default withLandingLayout(VideoTranslatorLandingPage);
